/** When a page's code cannot be loaded because a newer WOV was deployed
 *  while this one was open (its chunk names are gone), reload once to pick
 *  up the new build instead of showing the error screen (2026-10-02).
 *  A second failure within a minute shows the error, so a real outage can
 *  never become a reload loop. */

const KEY = "wov.chunkReloadAt";
const WINDOW_MS = 60_000;

/** The messages browsers give for a dynamic import or preload that failed. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? "");
  return /is not a valid JavaScript MIME type|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed/i.test(msg);
}

/** Whether a reload for a missing chunk may happen now (none in the last
 *  minute); records it when it may. */
export function mayReloadForChunk(now = Date.now(), store: Pick<Storage, "getItem" | "setItem"> | null = safeSession()): boolean {
  try {
    const last = Number(store?.getItem(KEY) ?? 0);
    if (last && now - last < WINDOW_MS) return false;
    store?.setItem(KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

function safeSession(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Reload for a missing chunk, once a minute at most. True when reloading. */
export function reloadForChunk(): boolean {
  if (!mayReloadForChunk()) return false;
  window.location.reload();
  return true;
}
